import React, { cloneElement, createContext, Dispatch, useReducer, useContext, Fragment } from 'react';
import produce from 'immer';
import { CircularProgress } from '@mui/material';

export interface IModalState {
  alert: string | undefined;
  content: string | undefined;
  width: number;
  height: number;
  timestamp: number;
  options?: any;
}

const ModalStateContext = createContext<IModalState | undefined>(undefined);

type IModalName = | 'ALERT'
  | 'UPDATE_META_ADS' | 'CREATE_META_TIME_GIFT' | 'UPDATE_META_VIP' | 'CREATE_RANDOM_BOX' | 'CREATE_MCODE'
  | 'CREATE_MAINTENANCE_SCHEDULE'
  | 'CREATE_MESSAGE_NOTICE' | 'CREATE_SYSTEM_NOTICE' | 'CREATE_TOUPP' | 'CREATE_OPERATOR_PAYMENT'
  | 'CREATE_BLACKLIST' | 'CREATE_WHITELIST' | 'CREATE_GATEWAY_OVERRIDE'
  | 'CREATE_LEAGUE_DATA' | 'CREATE_LEAGUE_SCHEDULE'
  | 'CREATE_ATTENDANCE_REWARD' | 'CREATE_ATTENDANCE'
  | 'CREATE_EVENT' | 'CREATE_EVENT_MULTI_ROUND' | 'CREATE_EVENT_SUB' | 'CREATE_EVENT_ROULETTE'
  | 'CREATE_BINGO_BALL_REWARDS' | 'CREATE_BINGO_BALL_LEAGUES' | 'CREATE_BINGO_BALL_SCHEDULE'
  | 'CREATE_CASH_MINE'
  | 'CREATE_MISSION' | 'CREATE_MISSION_SUB' | 'CREATE_MISSION_SEASONAL_CHAPTER'
  | 'CREATE_MINIGAME' | 'CREATE_MINIGAME_GACHA'
  | 'CREATE_ABTEST_TABLE' | 'CREATE_ABTEST_SCHEDULE' | 'CREATE_EVENT_DATA'
  | 'CREATE_PROMO_COUPON' | 'CREATE_INVITE_CONTROL' | 'CREATE_INVITE_QUEST'
  | 'CREATE_PRIZE_RANKING' | 'CREATE_PRIZE_RANKING_SUB'
  | 'CREATE_SEGMENT'
  | 'CREATE_ONELINK_SCHEDULE'
  | 'CREATE_WINNERS_TIP'
  | 'CREATE_SHOP_PRODUCT' | 'CREATE_SEQ_PRODUCT' | 'CREATE_VIP_PRODUCT' | 'CREATE_PAYBACK' | 'CREATE_PAYBACK_SUB'
  | 'BAN_PLAYER' | 'EVALUATE_WITHDRAWAL'
  | 'IMPORT_XLSX_DATA' | 'CREATE_TESTER'
  | 'CREATE_GAME_VERSION'

type IModalAction =
  | { type: 'OPEN'; name: IModalName; options?: any }
  | { type: 'CLOSE'; name?: IModalName, options?: any }

type ModalDispatch = Dispatch<IModalAction>;

const ModalDispatchContext = createContext<ModalDispatch | undefined>(
  undefined
);

const getInitialValues = (): IModalState => {
  return {
    alert: undefined,
    content: undefined,
    width: 0,
    height: 0,
    timestamp: 0
  };
};

const reducer = (state: IModalState, action: IModalAction): IModalState => {
  switch (action.type) {
    case 'OPEN':
      return produce(state, (draft: any) => {
        (action.name || '').match(/ALERT/) ?
          (draft.alert = action.name)
          : (draft.content = action.name);

        draft.options = action.options;
        draft.timestamp = new Date().getTime();
      });

    case 'CLOSE':
      return produce(state, (draft: any) => {
        switch (action.name) {
          case 'ALERT':
            draft.alert = undefined;
            !state.content && (draft.width = 0);
            !state.content && (draft.height = 0);
            state.content && (draft.options.open = true)
            break;

          default:
            draft.content = undefined;
            !state.alert && (draft.width = 0);
            !state.alert && (draft.height = 0);
            break;
        }

        draft.timestamp = new Date().getTime();
      });

    default:
      throw new Error('Unhandled action');
  }
};

export const ModalContextProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(reducer, getInitialValues());

  return (
    <ModalDispatchContext.Provider value={dispatch}>
      <ModalStateContext.Provider value={state}>
        {children}
      </ModalStateContext.Provider>
    </ModalDispatchContext.Provider>
  );
};

export const useModalState = () => {
  const state = useContext(ModalStateContext);

  if (state) {
    return state;
  } else {
    throw new Error('not-found modal-state');
  }
};

export const useModalDispatch = () => {
  const dispatch = useContext(ModalDispatchContext);

  if (dispatch) {
    return dispatch;
  } else {
    throw new Error('not-found modal-dispatch');
  }
};

export const useModalActions = () => {
  const dispatch = useContext(ModalDispatchContext);

  const openModalAlert = (options: {
    title?: any;
    description: any;
    callback?: (isConfirm: any) => void;
    isConfirm?: boolean;
    confirmLabel?: any;
    confirmInput?: string;
    isPassword?: boolean;
  }) => {
    dispatch && dispatch({ type: 'OPEN', name: 'ALERT', options: { ...options, open: false } });
  };

  const closeModalAlert = () => {
    dispatch && dispatch({ type: 'CLOSE', name: 'ALERT' });
  };

  const openModalLoading = (options?: { description: any }) => {
    dispatch &&
      dispatch({
        type: 'OPEN',
        name: 'ALERT',
        options: {
          description: (
            <Fragment>
              <CircularProgress />

              {options && (
                <div
                  style={{
                    display: 'block',
                    padding: '20px 0 0 0',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {Object.is(typeof options.description, 'string') ? (
                    <h5
                      style={{ fontWeight: 500 }}
                      dangerouslySetInnerHTML={{ __html: options.description }}
                    />
                  ) : (
                    cloneElement(options.description)
                  )}
                </div>
              )}
            </Fragment>
          ),
          isControl: false,
          isFixedWidth: false
        }
      });
  };

  const closeModalLoading = () => {
    dispatch && dispatch({ type: 'CLOSE', name: 'ALERT' });
  };

  const openModalContent = (name: IModalName, options?: any) => {
    dispatch && dispatch({ type: 'OPEN', name, options: { ...options, open: true } });
  };

  const closeModalContent = () => {
    dispatch && dispatch({ type: 'CLOSE' });
  };

  return {
    openModalAlert,
    closeModalAlert,
    openModalLoading,
    closeModalLoading,
    openModalContent,
    closeModalContent
  };
};
